/**
 * Google Analytics 4.
 *
 * Scope note, mirroring lib/analytics/google-ads.ts: this measures the PUBLIC
 * marketing site — how people find the landing page, which /for/* page they read,
 * where the funnel leaks before sign-up. PostHog remains the product analytics
 * for the authenticated app, and GA4 is deliberately never loaded there (see
 * lib/analytics/marketing-paths.ts).
 *
 * Everything here is inert until NEXT_PUBLIC_GA_MEASUREMENT_ID is set, so an
 * unconfigured environment — local dev included — loads no Google script and
 * measures nothing.
 */

/** e.g. "G-ABCD123456". Public by design: it ships in the client bundle. */
export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID ?? ''

/**
 * The `G-` check is not pedantry. A GA4 measurement id is always `G-…`, and the
 * three ids in this account's orbit look alike enough to paste into the wrong
 * variable: `AW-…` (Ads conversions), `GTM-…` (a Tag Manager container), `G-…`.
 * gtag accepts any of them in a `config` call and silently reports nothing to
 * the property you meant, which is a failure you only notice weeks later when
 * the reports are empty. Refusing to load is the louder, cheaper outcome.
 */
export const googleAnalyticsEnabled = () => GA_MEASUREMENT_ID.startsWith('G-')

/**
 * GA's official kill switch: a window global gtag checks before every hit.
 *
 * This is what actually enforces the marketing-only boundary at runtime, and it
 * is needed because a `<Script>` that unmounts does NOT unload — once gtag.js is
 * in the document it stays for the life of the tab. A visitor who lands on `/`
 * and then navigates client-side into the app is still carrying a live tag, and
 * GA4's Enhanced Measurement fires `page_view` on every `history.pushState`. So
 * without this flag, adding GA4 would quietly start reporting authenticated
 * route URLs — `/operations/report/1234`, `/submit/<token>` — to Google.
 *
 * Conditional rendering alone cannot fix that: the component would unmount and
 * lose the very effect that needs to run. The flag can be flipped from anywhere,
 * outlives the component, and is read by gtag itself.
 */
export const gaDisableFlag = (id: string = GA_MEASUREMENT_ID) => `ga-disable-${id}`

export function setGoogleAnalyticsCollection(enabled: boolean) {
  if (!googleAnalyticsEnabled()) return
  if (typeof window === 'undefined') return
  try {
    ;(window as unknown as Record<string, boolean>)[gaDisableFlag()] = !enabled
  } catch {
    // Analytics must never take the page down. Same contract as
    // lib/analytics/google-ads.ts and lib/posthog-server.ts.
  }
}
